import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { startOfMonth } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { formatDate } from '../../../i18n';
import { DashboardService } from '../../../services/dashboard.service';
import { AttendanceService } from '../../../services/attendance.service';
import { LeaveService } from '../../../services/leave.service';
import { TaskService } from '../../../services/task.service';
import { NoticeService } from '../../../services/notice.service';
import { AuthService } from '../../../services/auth.service';
import { UserDashboard } from '../../../interfaces/dashboard.interface';
import { Attendance } from '../../../interfaces/attendance.interface';
import { LeaveApplication, LeaveStatus } from '../../../interfaces/leave.interface';
import { ITask } from '../../../interfaces/task.interface';
import { INotice } from '../../../interfaces/notice.interface';
import { displayUsername } from '../../../utils/userUtils';
import StatTile from '../charts/StatTile';
import HBars from '../charts/HBars';
import Columns from '../charts/Columns';
import { useDashboardAccess } from '../useDashboardAccess';
import { DashSection, DashEmpty } from './DashSection';
import TeamToday from './TeamToday';
import {
    activeBalances, daysBetween, formatClock, formatDay, isApprovedOn, localIsoDate, summarizeMonth,
} from './personalDashboardUtils';

const STATUS_COLOR: Record<LeaveStatus, 'warning' | 'success' | 'error' | 'default'> = {
    [LeaveStatus.Pending]: 'warning',
    [LeaveStatus.Approved]: 'success',
    [LeaveStatus.Rejected]: 'error',
    [LeaveStatus.Cancelled]: 'default',
};

const fmtNum = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

const listDivider = <Box sx={{ borderTop: 1, borderColor: 'divider' }} />;

/**
 * Personal home for anyone without an admin/manager/accountant board: a KPI strip, charts and
 * lists built from the caller's own data, plus "My team — today" for department heads. Every
 * source is fetched independently, so one failing endpoint only removes the widgets that need it.
 */
const PersonalDashboard: React.FC = () => {
    const { t } = useTranslation('dashboard');
    const { user } = useAuth();
    const { enabledModules } = useDashboardAccess();
    const canOpenApprovals = usePermission(PERMISSIONS.SUBMENU_LEAVE_ALL_APPLICATIONS, false);
    const institutionId = AuthService.getSelectedInstitution();

    const [loading, setLoading] = useState(true);
    const [me, setMe] = useState<UserDashboard | null>(null);
    const [month, setMonth] = useState<Attendance[] | null>(null);
    const [apps, setApps] = useState<LeaveApplication[] | null>(null);
    const [tasks, setTasks] = useState<ITask[] | null>(null);
    const [notices, setNotices] = useState<INotice[] | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const now = new Date();
            const [meR, monthR, appsR, tasksR, noticesR] = await Promise.allSettled([
                DashboardService.getMyDashboard(institutionId),
                AttendanceService.getLoggedInUserAttendanceList(startOfMonth(now), now),
                LeaveService.getLeaveApplications(0, 20),
                TaskService.myTasks(institutionId, 0, 20, ['OPEN', 'IN_PROGRESS']),
                NoticeService.feed(institutionId, 0, 5),
            ]);
            if (cancelled) return;
            if (meR.status === 'fulfilled') setMe(meR.value);
            if (monthR.status === 'fulfilled') setMonth(monthR.value.data.content);
            if (appsR.status === 'fulfilled') setApps(appsR.value.content);
            if (tasksR.status === 'fulfilled') setTasks(tasksR.value.content);
            if (noticesR.status === 'fulfilled') setNotices(noticesR.value.content);
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [institutionId]);

    const today = localIsoDate();

    const balances = useMemo(() => (me ? activeBalances(me.leaveBalances) : []), [me]);
    const summary = useMemo(() => (month ? summarizeMonth(month) : null), [month]);
    const holidays = useMemo(() => (me?.upcomingHolidays ?? [])
        .filter(h => h.endDate >= today)
        .sort((a, b) => a.startDate.localeCompare(b.startDate))
        .slice(0, 5), [me, today]);
    const nextHoliday = holidays[0] ?? null;
    const onLeaveToday = useMemo(() => !!apps?.some(a => isApprovedOn(a, today)), [apps, today]);
    const pendingCount = me?.myPendingLeaves ?? apps?.filter(a => a.leaveStatus === LeaveStatus.Pending).length ?? null;

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
    }
    if (!me && !month && !apps) {
        return <Alert severity="error" sx={{ mt: 2 }}>{t('loadFailed')}</Alert>;
    }

    const showLeave = enabledModules.has('leave');
    const showAttendance = enabledModules.has('attendance');

    const displayName = user?.name || displayUsername(user?.username);
    const isHead = user?.isDepartmentAdmin === true;

    const todayRow = me?.todayAttendance ?? null;
    let statusValue = t('personal.notClockedIn');
    let statusSub: string | undefined;
    let statusTone: 'default' | 'good' | 'warning' = 'warning';
    if (todayRow) {
        statusTone = 'good';
        // Clock-in stamps endTime = startTime; only a later out-punch means the day is closed.
        const clockedOut = !!todayRow.endTime && todayRow.endTime !== todayRow.startTime;
        statusValue = clockedOut ? t('personal.clockedOut') : t('personal.clockedIn');
        statusSub = clockedOut && todayRow.endTime
            ? t('personal.inOutAt', { in: formatClock(todayRow.startTime), out: formatClock(todayRow.endTime) })
            : t('personal.inAt', { time: formatClock(todayRow.startTime) });
    } else if (onLeaveToday) {
        statusValue = t('personal.onLeave');
        statusTone = 'default';
    }

    const leaveRemaining = balances.reduce((s, b) => s + b.remaining, 0);
    const leaveAllocated = balances.reduce((s, b) => s + b.allocated, 0);
    const nextHolidayDays = nextHoliday ? daysBetween(today, nextHoliday.startDate) : null;

    const openTasks = tasks ?? [];
    const recentApps = (apps ?? []).slice(0, 5);

    const personalActions = (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {showLeave && (
                <Button component={RouterLink} to="/leave-applications" size="small" variant={isHead ? 'outlined' : 'contained'}>
                    {t('personal.applyLeave')}
                </Button>
            )}
            {showAttendance && (
                <Button component={RouterLink} to="/attendance" size="small" variant="outlined">{t('personal.myAttendance')}</Button>
            )}
        </Stack>
    );

    return (
        <Box sx={{ p: { xs: 0, md: 1 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold">
                        {displayName ? t('personal.hello', { name: displayName }) : t('personal.helloNoName')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {formatDate(new Date(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </Typography>
                </Box>
                {/* A department head's header leads to team work; their own self-service actions sit
                    with the "Me" section below. Everyone else keeps the personal actions up here. */}
                {isHead
                    ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {canOpenApprovals && showLeave && (
                                <Button component={RouterLink} to="/all-leave-applications" size="small" variant="contained">
                                    {t('personal.leaveApprovals')}
                                </Button>
                            )}
                            <Button component={RouterLink} to="/departments" size="small" variant="outlined">{t('personal.myDepartments')}</Button>
                        </Stack>
                    )
                    : personalActions}
            </Stack>

            {isHead && institutionId && showAttendance && (
                <TeamToday institutionId={institutionId} approvalsPath={canOpenApprovals ? '/all-leave-applications' : null} />
            )}

            {isHead && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="h6">{t('personal.me')}</Typography>
                    {personalActions}
                </Stack>
            )}
            <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
                <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {me && <StatTile label={t('personal.today')} value={statusValue} sub={statusSub} tone={statusTone} />}
                    {summary && <StatTile label={t('personal.daysPresent')} value={summary.daysPresent} sub={t('personal.thisMonth')} />}
                    {summary && (
                        <StatTile label={t('personal.lateDays')} value={summary.lateDays} sub={t('personal.thisMonth')} tone={summary.lateDays > 0 ? 'warning' : 'good'} />
                    )}
                    {me && balances.length > 0 && (
                        <StatTile label={t('personal.leaveLeft')} value={fmtNum(leaveRemaining)} sub={t('personal.ofDays', { n: fmtNum(leaveAllocated) })} />
                    )}
                    {pendingCount != null && (
                        <StatTile label={t('personal.pendingLeave')} value={pendingCount} tone={pendingCount > 0 ? 'warning' : 'default'} sub={t('personal.awaitingApproval')} />
                    )}
                    {me && (
                        <StatTile
                            label={t('personal.nextHoliday')} value={nextHoliday ? nextHoliday.name : t('personal.none')}
                            sub={nextHoliday && nextHolidayDays != null
                                ? (nextHolidayDays <= 0
                                    ? t('personal.holidayToday', { date: formatDay(nextHoliday.startDate) })
                                    : t('personal.holidayIn', { date: formatDay(nextHoliday.startDate), count: nextHolidayDays }))
                                : t('personal.noUpcomingHoliday')}
                        />
                    )}
                </Stack>
            </Paper>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(2, minmax(0,1fr))' }, gap: 2, mb: 2 }}>
                {summary && (
                    <DashSection title={t('personal.myHours')} subtitle={t('personal.myHoursSub')}>
                        {summary.hoursPerDay.length === 0
                            ? <DashEmpty text={t('personal.noCompletedDays')} />
                            : <Columns data={summary.hoursPerDay} yFmt={n => t('widgets.attendance.hours', { hours: fmtNum(n) })} />}
                    </DashSection>
                )}
                {me && balances.length > 0 && (
                    <DashSection title={t('personal.leaveBalance')} subtitle={t('personal.leaveBalanceSub')}>
                        <HBars
                            data={balances.map(b => ({
                                label: b.leaveType, value: b.remaining,
                                tooltip: [
                                    { label: t('personal.remaining'), value: fmtNum(b.remaining) },
                                    { label: t('personal.used'), value: fmtNum(b.availed) },
                                    { label: t('personal.allocated'), value: fmtNum(b.allocated) },
                                ],
                            }))}
                            vFmt={fmtNum}
                        />
                    </DashSection>
                )}
                {me && (
                    <DashSection title={t('personal.upcomingHolidays')}>
                        {holidays.length === 0 ? <DashEmpty text={t('personal.noUpcomingHolidays')} /> : (
                            <Stack spacing={1} divider={listDivider} data-testid="holiday-list">
                                {holidays.map(h => (
                                    <Stack key={h.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{h.name}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {h.endDate !== h.startDate
                                                ? t('personal.dateRange', { from: formatDay(h.startDate), to: formatDay(h.endDate) })
                                                : formatDay(h.startDate)}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </DashSection>
                )}
                {apps && showLeave && (
                    <DashSection title={t('personal.recentLeave')} subtitle={t('personal.recentLeaveSub')}>
                        {recentApps.length === 0 ? <DashEmpty text={t('personal.noLeaveApplications')} /> : (
                            <Stack spacing={1} divider={listDivider} data-testid="recent-leave">
                                {recentApps.map(a => (
                                    <Stack key={a.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Link component={RouterLink} to="/leave-applications" underline="hover" sx={{ fontWeight: 600 }}>{a.leaveType}</Link>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('personal.dateRange', { from: formatDay(a.startDate), to: formatDay(a.endDate) })}
                                        </Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Chip size="small" label={t(`leave:status.${a.leaveStatus}`)} color={STATUS_COLOR[a.leaveStatus] ?? 'default'} />
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </DashSection>
                )}
                {tasks && openTasks.length > 0 && (
                    <DashSection title={t('personal.openTasks')} subtitle={t('personal.openTasksSub')}>
                        <Stack spacing={1} divider={listDivider} data-testid="my-tasks">
                            {openTasks.slice(0, 5).map(task => (
                                <Stack key={task.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Link component={RouterLink} to="/tasks" underline="hover" sx={{ fontWeight: 600 }}>{task.title}</Link>
                                    <Box sx={{ flex: 1 }} />
                                    {task.dueDate && (
                                        <Typography variant="body2" color="text.secondary">{t('personal.due', { date: formatDay(task.dueDate) })}</Typography>
                                    )}
                                    {task.isOverdue && <Chip size="small" color="error" label={t('personal.overdue')} />}
                                </Stack>
                            ))}
                        </Stack>
                    </DashSection>
                )}
                {notices && notices.length > 0 && (
                    <DashSection title={t('personal.recentNotices')}>
                        <Stack spacing={1} divider={listDivider} data-testid="my-notices">
                            {notices.slice(0, 4).map(n => (
                                <Stack key={n.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Link component={RouterLink} to="/notices" underline="hover" sx={{ fontWeight: n.hasCurrentUserRead ? 400 : 600 }}>{n.title}</Link>
                                    <Box sx={{ flex: 1 }} />
                                    <Typography variant="body2" color="text.secondary">{formatDate(n.publishAt)}</Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </DashSection>
                )}
            </Box>
        </Box>
    );
};

export default PersonalDashboard;
